import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Grid,
  Button,
  Alert
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SecurityIcon from '@mui/icons-material/Security';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Admin emails are now loaded dynamically from Firestore
import { useAdminEmails } from '../../hooks/useAdminEmails';
import { AdminEmailsManager } from '../../components/admin/AdminEmailsManager';

const SettingsPage: React.FC = () => {
  const { state } = useAuth();
  
  // Check if the current user is an admin
  const { adminEmails, loading: adminLoading } = useAdminEmails();
const isAdmin = state.user && adminEmails.includes(state.user.email);

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                General Settings
              </Typography>
              <Divider sx={{ my: 2 }} />
              <List>
                <ListItem component={RouterLink} to="/profile" sx={{ cursor: 'pointer' }}>
                  <ListItemIcon>
                    <AccountCircleIcon />
                  </ListItemIcon>
                  <ListItemText primary="Profile Settings" secondary="Update your personal information" />
                </ListItem>
                <ListItem component={RouterLink} to="/broker-config" sx={{ cursor: 'pointer' }}>
                  <ListItemIcon>
                    <SecurityIcon />
                  </ListItemIcon>
                  <ListItemText primary="Exchange Connections" secondary="Manage your exchange API keys" />
                </ListItem>

                <ListItem sx={{ cursor: 'pointer' }}>
                  <ListItemIcon>
                    <NotificationsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Notifications" secondary="Configure your notification preferences" />
                </ListItem>

                {/* Admin grouped dashboard access */}
                {isAdmin && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2, mb: 1 }}>
                      Admin Dashboards
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">Stock Brokers</Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        component={RouterLink}
                        to="/admin-robinhood"
                        startIcon={<AdminPanelSettingsIcon />}
                        sx={{ mb: 1, textAlign: 'left', justifyContent: 'flex-start', maxWidth: 260 }}
                      >
                        Robinhood Admin Dashboard
                      </Button>
                      <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>Crypto Exchanges</Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        component={RouterLink}
                        to="/admin-kraken"
                        startIcon={<AdminPanelSettingsIcon />}
                        sx={{ mb: 1, textAlign: 'left', justifyContent: 'flex-start', maxWidth: 260 }}
                      >
                        Kraken Admin Dashboard
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        component={RouterLink}
                        to="/admin-binanceus"
                        startIcon={<AdminPanelSettingsIcon />}
                        sx={{ mb: 1, textAlign: 'left', justifyContent: 'flex-start', maxWidth: 260 }}
                      >
                        Binance US Admin Dashboard
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        component={RouterLink}
                        to="/admin-coinbase"
                        startIcon={<AdminPanelSettingsIcon />}
                        sx={{ mb: 1, textAlign: 'left', justifyContent: 'flex-start', maxWidth: 260 }}
                      >
                        Coinbase Admin Dashboard
                      </Button>
                    </Box>
                  </>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Admin Panel (only visible to admins) */}
        {isAdmin && (
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <AdminPanelSettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Admin Panel
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  These pages display completely unmodified raw data directly from the exchange APIs, 
                  showing exactly what's coming from the APIs without any transformations.
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>
                  Raw API Data Access
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      component={RouterLink}
                      to="/admin-kraken"
                      startIcon={<VisibilityIcon />}
                    >
                      Kraken Admin Dashboard
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      component={RouterLink}
                      to="/admin-binanceus"
                      startIcon={<VisibilityIcon />}
                    >
                      Binance US Admin Dashboard
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      component={RouterLink}
                      to="/admin-coinbase"
                      startIcon={<VisibilityIcon />}
                    >
                      Coinbase Admin Dashboard
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      component={RouterLink}
                      to="/admin-uniswap"
                      startIcon={<VisibilityIcon />}
                    >
                      Uniswap Admin Dashboard
                    </Button>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 3 }}>
                  <Alert severity="info">
                    These admin pages display completely unmodified raw data from the exchange APIs. 
                    For Binance US, assets are shown in the "balances" array exactly as returned by the API.
                  </Alert>
                </Box>
                
                {/* Admin Email Management */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Admin Access Management
                  </Typography>
                  <AdminEmailsManager />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default SettingsPage;
